import { SCREEN_IDS } from "../../game/core/constants.js";
import { DEBUG_SPAWN_STAT_DATASETS, delay } from "./shared.js";

export const appShellEventMethods = {
  getDesktopApi() {
    return globalThis.ashRun84Api ?? null;
  },

  downloadMapEditorJson(exportedMap) {
    const blob = new Blob([exportedMap.text], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = exportedMap.filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  logDesktopDialogFallback(action, error) {
    console.warn(
      `Map editor ${action} dialog unavailable in the current Electron main process. Falling back.`,
      error
    );
  },

  syncMapEditorNameDraft(value) {
    const headerTitle = this.root.querySelector(
      "[data-map-editor-live-name], .map-editor-meta__title, .map-editor-header__copy h2"
    );

    if (headerTitle) {
      headerTitle.textContent = String(value ?? "").trimStart() || "Untitled Map";
    }
  },

  getDebugField(field, fallback = "") {
    return this.root.querySelector(`[data-debug-field="${field}"]`)?.value ?? fallback;
  },

  getDebugNumberField(field, fallback = 0) {
    const parsed = Number(this.getDebugField(field, fallback));
    return Number.isFinite(parsed) ? parsed : fallback;
  },

  syncDebugSpawnStatFields() {
    const unitTypeSelect = this.root.querySelector('[data-debug-field="spawn-unit-type"]');
    const selectedOption = unitTypeSelect?.selectedOptions?.[0];

    if (!selectedOption) {
      return;
    }

    for (const [field, datasetKey] of DEBUG_SPAWN_STAT_DATASETS) {
      const input = this.root.querySelector(`[data-debug-field="${field}"]`);

      if (input) {
        input.value = selectedOption.dataset[datasetKey] ?? "";
      }
    }

    const gearSelect = this.root.querySelector('[data-debug-field="spawn-gear-slot"]');

    if (!gearSelect) {
      return;
    }

    const selectedFamily = selectedOption.dataset.family ?? "";

    for (const option of gearSelect.options) {
      if (!option.value) {
        option.disabled = false;
        continue;
      }

      option.disabled =
        Boolean(option.dataset.eligibleFamily) && option.dataset.eligibleFamily !== selectedFamily;
    }

    if (gearSelect.selectedOptions[0]?.disabled) {
      gearSelect.value = "";
    }
  },

  async handleContextMenu(event) {
    if (
      this.latestState?.screen !== SCREEN_IDS.BATTLE ||
      !event.target?.closest?.(".battle-shell")
    ) {
      return;
    }

    event.preventDefault();
    await this.controller.handleBattleContextAction();
  },

  handleToggle(event) {
    if (this.latestState?.screen !== SCREEN_IDS.MAP_EDITOR) {
      return;
    }

    const accordion = event.target;

    if (!accordion?.matches?.("details[data-map-editor-accordion], details[data-map-editor-load-group]")) {
      return;
    }

    this.captureMapEditorUiState();
  },

  async handleClick(event) {
    const trigger = event.target.closest("[data-action]");

    if (!trigger || !this.latestState) {
      return;
    }

    const { action, commanderId, slotId, unitTypeId } = trigger.dataset;

    switch (action) {
      case "open-new-run":
        this.controller.openNewRun();
        break;
      case "open-continue":
        this.controller.openContinue();
        break;
      case "open-skirmish":
        this.controller.openSkirmish();
        break;
      case "open-tutorial":
        this.controller.openTutorial();
        break;
      case "start-tutorial":
        this.controller.startTutorialBattle();
        break;
      case "tutorial-next":
        this.controller.continueTutorialStep();
        break;
      case "skip-tutorial":
        this.controller.skipTutorial();
        break;
      case "tutorial-epilogue":
        this.controller.openTutorialEpilogue();
        break;
      case "open-map-editor":
        this.controller.openMapEditor();
        break;
      case "open-options":
        this.controller.openOptions();
        break;
      case "open-progression":
        this.controller.openProgression();
        break;
      case "open-run-loadout":
        this.controller.openRunLoadout();
        break;
      case "apply-display-settings":
        await this.applyDisplaySettings();
        break;
      case "keep-display-settings":
        await this.keepDisplaySettings();
        break;
      case "revert-display-settings":
        await this.revertDisplaySettings();
        break;
      case "return-windowed-display":
        await this.returnToWindowedDisplay();
        break;
      case "purchase-unit-unlock":
        this.controller.purchaseUnitUnlock(unitTypeId);
        break;
      case "purchase-card-unlock":
        this.controller.purchaseRunCardUnlock(trigger.dataset.cardId);
        break;
      case "open-debug-run":
        this.controller.startDebugRun();
        break;
      case "back-to-title":
        await this.controller.returnToTitle();
        break;
      case "pause-battle":
        this.controller.openPauseMenu();
        break;
      case "resume-battle":
        this.controller.closePauseMenu();
        break;
      case "prompt-abandon-run":
        this.controller.promptAbandonRun();
        break;
      case "cancel-abandon-run":
        this.controller.cancelAbandonRun();
        break;
      case "confirm-abandon-run":
        await this.controller.abandonRun();
        break;
      case "acknowledge-level-up": {
        const overlay = this.root.querySelector(".battle-overlay--level-up");
        const card = overlay?.querySelector(".overlay-card--level-up");
        overlay?.classList.add("battle-overlay--closing");
        card?.classList.add("overlay-card--closing");
        await delay(220);
        await this.controller.acknowledgeLevelUp();
        break;
      }
      case "quit-game":
        await this.controller.quitGame();
        break;
      case "select-commander":
        if (
          this.commanderSliderSuppressClick &&
          trigger.closest('[data-role="commander-slider"]')
        ) {
          this.commanderSliderSuppressClick = false;
          event.preventDefault();
          return;
        }
        if (trigger.getAttribute("aria-disabled") === "true") {
          return;
        }
        this.controller.selectCommander(commanderId);
        break;
      case "commander-slider-prev":
        this.scrollCommanderSlider(-1);
        break;
      case "commander-slider-next":
        this.scrollCommanderSlider(1);
        break;
      case "scroll-skirmish-commanders":
        this.scrollCommanderSliderById(
          trigger.dataset.commanderSliderId,
          Number(trigger.dataset.skirmishDirection)
        );
        break;
      case "select-slot":
        this.controller.selectSlot(slotId);
        break;
      case "start-run":
        await this.controller.startNewRun();
        break;
      case "back-to-commander-select":
        this.controller.returnToCommanderSelect();
        break;
      case "run-loadout-add":
        this.controller.addRunLoadoutUnit(unitTypeId);
        break;
      case "run-loadout-remove":
        this.controller.removeRunLoadoutUnit(unitTypeId);
        break;
      case "select-skirmish-player-commander":
        if (
          this.commanderSliderSuppressClick &&
          trigger.closest('[data-role="commander-slider"]')
        ) {
          this.commanderSliderSuppressClick = false;
          event.preventDefault();
          return;
        }
        if (trigger.getAttribute("aria-disabled") === "true") {
          return;
        }
        this.controller.updateSkirmishSetup({ playerCommanderId: commanderId });
        break;
      case "select-skirmish-enemy-commander":
        if (
          this.commanderSliderSuppressClick &&
          trigger.closest('[data-role="commander-slider"]')
        ) {
          this.commanderSliderSuppressClick = false;
          event.preventDefault();
          return;
        }
        if (trigger.getAttribute("aria-disabled") === "true") {
          return;
        }
        this.controller.updateSkirmishSetup({ enemyCommanderId: commanderId });
        break;
      case "select-skirmish-map":
        this.controller.updateSkirmishSetup({ mapId: trigger.dataset.mapId });
        break;
      case "skirmish-next-step":
        this.controller.updateSkirmishSetup({ step: "map" });
        break;
      case "skirmish-previous-step":
        this.controller.updateSkirmishSetup({ step: "commanders" });
        break;
      case "start-skirmish":
        await this.controller.startSkirmish();
        break;
      case "map-editor-new":
        this.controller.resetMapEditor();
        break;
      case "map-editor-select-tool":
        this.controller.setMapEditorTool(trigger.dataset.mapEditorTool);
        break;
      case "map-editor-select-terrain":
        this.controller.selectMapEditorTerrain(trigger.dataset.terrainId);
        break;
      case "map-editor-select-building":
        this.controller.selectMapEditorBuildingType(trigger.dataset.buildingType);
        break;
      case "map-editor-select-building-owner":
        this.controller.selectMapEditorBuildingOwner(trigger.dataset.buildingOwner);
        break;
      case "map-editor-select-unit":
        this.controller.selectMapEditorUnitType(trigger.dataset.unitTypeId);
        break;
      case "map-editor-select-unit-owner":
        this.controller.selectMapEditorUnitOwner(trigger.dataset.unitOwner);
        break;
      case "map-editor-select-reinforcement-unit":
        this.controller.selectMapEditorReinforcementUnitType(trigger.dataset.unitTypeId);
        break;
      case "map-editor-select-reinforcement-wave":
        this.controller.selectMapEditorReinforcementWave(trigger.dataset.reinforcementWaveId);
        break;
      case "map-editor-add-reinforcement-wave":
        this.controller.addMapEditorReinforcementWave();
        break;
      case "map-editor-delete-reinforcement-wave":
        this.controller.deleteSelectedMapEditorReinforcementWave();
        break;
      case "map-editor-reinforcement-use-selected-unit":
        this.controller.setMapEditorReinforcementTargetFromSelectedUnit();
        break;
      case "map-editor-restore-last-terrain":
        this.controller.restoreLastMapEditorTerrain?.();
        break;
      case "map-editor-restore-last-building":
        this.controller.restoreLastMapEditorBuilding?.();
        break;
      case "map-editor-restore-last-unit":
        this.controller.restoreLastMapEditorUnit?.();
        break;
      case "map-editor-set-mirror-mode":
        this.controller.setMapEditorMirrorMode(trigger.dataset.mirrorMode);
        break;
      case "map-editor-set-variant-stage":
        this.controller.setMapEditorVariantStage(Number(trigger.dataset.variantStage));
        break;
      case "map-editor-undo":
        this.controller.undoMapEditorHistory?.();
        break;
      case "map-editor-request-history-revert":
        this.controller.requestMapEditorHistoryRevert?.(Number(trigger.dataset.historyIndex));
        break;
      case "map-editor-confirm-history-revert":
        this.controller.confirmMapEditorHistoryRevert?.();
        break;
      case "map-editor-cancel-history-revert":
        this.controller.cancelMapEditorHistoryRevert?.();
        break;
      case "map-editor-goal-use-selected-building":
        this.controller.setMapEditorGoalTargetFromSelectedBuilding();
        break;
      case "map-editor-goal-clear-target":
        this.controller.clearMapEditorGoalTarget();
        break;
      case "map-editor-import":
        await this.controller.openMapEditorLoadDialog?.();
        break;
      case "map-editor-close-load-dialog":
        this.controller.closeMapEditorLoadDialog?.();
        break;
      case "map-editor-select-load-entry":
        this.controller.selectMapEditorLoadDialogEntry?.(trigger.dataset.mapRelativePath);
        break;
      case "map-editor-confirm-load":
        await this.controller.confirmMapEditorLoadDialog?.();
        break;
      case "map-editor-export": {
        const saveResult = await this.controller.saveMapEditorMap?.();

        if (saveResult?.mode === "download" && saveResult.exportedMap) {
          if (saveResult.warning) {
            this.logDesktopDialogFallback("export", saveResult.warning);
          }
          this.downloadMapEditorJson(saveResult.exportedMap);
          this.controller.showToast?.({
            title: "Map downloaded",
            message: `${saveResult.exportedMap.filename} was downloaded through the browser fallback.`,
            tone: "success"
          });
        }
        break;
      }
      case "load-slot":
        await this.controller.loadSlot(slotId);
        break;
      case "delete-slot":
        await this.controller.deleteSlot(slotId);
        break;
      case "end-turn":
        await this.controller.endTurn();
        break;
      case "activate-power":
        await this.controller.activatePower();
        break;
      case "recruit-unit":
        await this.controller.recruitUnit(unitTypeId);
        break;
      case "select-next-unit":
        await this.controller.selectNextReadyUnit();
        break;
      case "open-run-cards":
        this.controller.openRunCardsPanel();
        break;
      case "close-run-cards":
        this.controller.closeRunCardsPanel();
        break;
      case "wait-unit":
        await this.controller.waitWithSelectedUnit();
        break;
      case "begin-attack":
        await this.controller.beginSelectedAttack();
        break;
      case "cancel-attack":
        await this.controller.cancelSelectedAttack();
        break;
      case "cancel-transport-choice":
      case "cancel-support-choice":
      case "cancel-medpack-choice":
      case "cancel-extinguish-choice":
      case "cancel-unload-choice":
        await this.controller.handleBattleContextAction();
        break;
      case "capture-building":
        await this.controller.captureWithSelectedUnit();
        break;
      case "use-supply":
        await this.controller.useSelectedSupply();
        break;
      case "rescue-hostage":
        await this.controller.rescueHostageWithSelectedUnit();
        break;
      case "drop-off-hostage":
        await this.controller.dropOffHostageWithSelectedUnit();
        break;
      case "use-support":
        await this.controller.useSelectedSupportAbility();
        break;
      case "use-medpack":
        await this.controller.useSelectedMedpack();
        break;
      case "use-extinguish":
        await this.controller.useSelectedExtinguish();
        break;
      case "enter-transport":
        await this.controller.enterSelectedTransport();
        break;
      case "begin-unload":
        await this.controller.beginSelectedUnload();
        break;
      case "redo-move":
        await this.controller.redoSelectedMove();
        break;
      case "advance-run":
        await this.controller.advanceRun();
        break;
      case "select-run-reward":
        await this.controller.selectRunReward(trigger.dataset.rewardId);
        break;
      case "equip-run-gear":
        await this.controller.equipPendingRunGear(trigger.dataset.unitId);
        break;
      case "discard-run-gear":
        await this.controller.discardPendingRunGear();
        break;
      case "debug-spawn-unit":
        await this.controller.debugSpawnUnit({
          owner: this.getDebugField("spawn-owner", "player"),
          unitTypeId: this.getDebugField("spawn-unit-type", "grunt"),
          x: this.getDebugNumberField("spawn-x", 0),
          y: this.getDebugNumberField("spawn-y", 0),
          gearSlot: this.getDebugField("spawn-gear-slot", ""),
          stats: {
            attack: this.getDebugNumberField("spawn-attack", NaN),
            armor: this.getDebugNumberField("spawn-armor", NaN),
            maxHealth: this.getDebugNumberField("spawn-max-health", NaN),
            movement: this.getDebugNumberField("spawn-movement", NaN),
            minRange: this.getDebugNumberField("spawn-min-range", NaN),
            maxRange: this.getDebugNumberField("spawn-max-range", NaN),
            staminaMax: this.getDebugNumberField("spawn-max-stamina", NaN),
            ammoMax: this.getDebugNumberField("spawn-max-ammo", NaN),
            luck: this.getDebugNumberField("spawn-luck", NaN)
          }
        });
        break;
      case "debug-apply-selected-stats":
        await this.controller.debugApplySelectedUnitStats({
          hp: this.getDebugNumberField("unit-hp", NaN),
          maxHealth: this.getDebugNumberField("unit-max-health", NaN),
          attack: this.getDebugNumberField("unit-attack", NaN),
          armor: this.getDebugNumberField("unit-armor", NaN),
          movement: this.getDebugNumberField("unit-movement", NaN),
          minRange: this.getDebugNumberField("unit-min-range", NaN),
          maxRange: this.getDebugNumberField("unit-max-range", NaN),
          stamina: this.getDebugNumberField("unit-stamina", NaN),
          staminaMax: this.getDebugNumberField("unit-max-stamina", NaN),
          ammo: this.getDebugNumberField("unit-ammo", NaN),
          ammoMax: this.getDebugNumberField("unit-max-ammo", NaN),
          luck: this.getDebugNumberField("unit-luck", NaN),
          level: this.getDebugNumberField("unit-level", NaN),
          experience: this.getDebugNumberField("unit-experience", NaN),
          gearSlot: this.getDebugField("unit-gear-slot", "")
        });
        break;
      case "debug-apply-commanders":
        await this.controller.debugSetCommanders({
          playerCommanderId: this.getDebugField("player-commander", "atlas"),
          enemyCommanderId: this.getDebugField("enemy-commander", "viper"),
          enemyAiArchetype: this.getDebugField("enemy-ai-archetype", "balanced")
        });
        break;
      case "debug-load-map":
        this.controller.startDebugRun({
          mapId: this.getDebugField("sandbox-map", ""),
          keepPauseMenuOpen: true
        });
        break;
      case "debug-full-charge-player":
        await this.controller.debugSetCharge("player", 9999);
        break;
      case "debug-full-charge-enemy":
        await this.controller.debugSetCharge("enemy", 9999);
        break;
      case "debug-refresh-player-actions":
        await this.controller.debugRefreshActions("player");
        break;
      case "debug-refresh-enemy-actions":
        await this.controller.debugRefreshActions("enemy");
        break;
      case "debug-add-run-card":
        await this.controller.debugAddRunCard(this.getDebugField("run-card-id", ""));
        break;
      case "debug-clear-run-cards":
        await this.controller.debugClearRunCards();
        break;
      default:
        break;
    }
  },

  async handleChange(event) {
    if (this.handleDisplayOptionChange?.(event)) {
      return;
    }

    const skirmishField = event.target.dataset.skirmishField;

    if (skirmishField) {
      await this.controller.updateSkirmishSetup({
        [skirmishField]: Number(event.target.value)
      });
      return;
    }

    const mapEditorField = event.target.dataset.mapEditorField;

    if (mapEditorField) {
      this.setInputMode("mouse");
      this.controller.updateMapEditorField(mapEditorField, event.target.value);

      return;
    }

    if (event.target.dataset.debugField === "spawn-unit-type") {
      this.syncDebugSpawnStatFields();
      return;
    }

    const optionKey = event.target.dataset.option;

    if (!optionKey) {
      return;
    }

    const nextValue =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.type === "range" || event.target.type === "number"
          ? Number(event.target.value)
          : event.target.value;

    await this.controller.updateOptions({
      [optionKey]: nextValue
    });
  },

  handleInput(event) {
    const skirmishField = event.target.dataset.skirmishField;

    if (skirmishField) {
      const output = this.root.querySelector(`[data-skirmish-output="${skirmishField}"]`);

      if (output) {
        output.textContent = event.target.value;
      }

      return;
    }

    const mapEditorField = event.target.dataset.mapEditorField;

    if (!mapEditorField) {
      return;
    }

    this.setInputMode("mouse");

    if (mapEditorField === "name") {
      this.controller.updateMapEditorField(mapEditorField, event.target.value, { emit: false });
      this.syncMapEditorNameDraft(event.target.value);
    }
  }
};
